import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import './VideoPlayer.css';

const VideoPlayer = forwardRef(({ 
  videoSrc,
  isLive = false, 
  liveFrame = null,
  onTimeUpdate,
  onLoadedMetadata,
  className = ''
}, ref) => {
  const videoRef = useRef(null);
  const iconTimeoutRef = useRef(null);
  const rafRef = useRef(null);
  const [showIcon, setShowIcon] = useState(null);
  const [iconFading, setIconFading] = useState(false);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (iconTimeoutRef.current) {
      clearTimeout(iconTimeoutRef.current);
    }

    if (video.paused) {
      // If video ended, restart from beginning
      if (video.ended) {
        video.currentTime = 0;
      }
      
      // Handle play promise to avoid interruption errors
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Auto-play was prevented or interrupted
          console.log('Play interrupted:', error);
        });
      }
      
      setShowIcon('play');
      setIconFading(false);
      
      setTimeout(() => setIconFading(true), 10);
      
      iconTimeoutRef.current = setTimeout(() => {
        setShowIcon(null);
        setIconFading(false);
      }, 800);
    } else {
      video.pause();
      setShowIcon('pause');
      setIconFading(false);
      
      setTimeout(() => setIconFading(true), 10);
      
      iconTimeoutRef.current = setTimeout(() => {
        setShowIcon(null);
        setIconFading(false);
      }, 800);
    }
  };

  useImperativeHandle(ref, () => ({
    seekTo: (time) => {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
        
        // Handle play promise to avoid interruption errors
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Play interrupted:', error);
          });
        }
        
        if (iconTimeoutRef.current) {
          clearTimeout(iconTimeoutRef.current);
        }
        setShowIcon('play');
        setIconFading(false);
        
        setTimeout(() => setIconFading(true), 10);
        
        iconTimeoutRef.current = setTimeout(() => {
          setShowIcon(null);
          setIconFading(false);
        }, 800);
      }
    },
    togglePlayPause,
    play: () => {
      if (videoRef.current) {
        // Handle play promise to avoid interruption errors
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Play interrupted:', error);
          });
        }
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    getCurrentTime: () => {
      return videoRef.current?.currentTime || 0;
    },
    getDuration: () => {
      return videoRef.current?.duration || 0;
    },
    setPlaybackRate: (rate) => {
      if (videoRef.current) {
        videoRef.current.playbackRate = rate;
      }
    },
    get paused() {
      return videoRef.current?.paused;
    },
    get videoElement() {
      return videoRef.current;
    }
  }));

  useEffect(() => {
    return () => {
      if (iconTimeoutRef.current) {
        clearTimeout(iconTimeoutRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Use requestAnimationFrame for smooth progress updates (60fps)
  useEffect(() => {
    const updateTime = () => {
      if (videoRef.current && onTimeUpdate && !videoRef.current.paused && !videoRef.current.ended) {
        onTimeUpdate(videoRef.current.currentTime);
      }
      rafRef.current = requestAnimationFrame(updateTime);
    };

    rafRef.current = requestAnimationFrame(updateTime);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [onTimeUpdate]);


  const handleTimeUpdate = (e) => {
    // Fallback for browsers that don't support RAF properly
    // RAF hook above handles smooth updates at 60fps
    if (onTimeUpdate && (!rafRef.current)) {
      onTimeUpdate(e.target.currentTime);
    }
  };

  const handleLoadedMetadata = (e) => {
    if (onLoadedMetadata) {
      onLoadedMetadata(e.target.duration);
    }
  };

  if (isLive) {
    return (
      <div className={`zibby-video-container ${className}`}>
        <div className="zibby-live-video-container">
          {liveFrame ? (
            <img src={liveFrame} alt="Live feed" className="zibby-live-frame" />
          ) : (
            <div className="zibby-video-placeholder zibby-video-placeholder--live" role="status">
              <div className="zibby-video-placeholder__content">
                <span className="zibby-video-placeholder__pulse" aria-hidden />
                <p>Waiting for browser…</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!videoSrc) {
    return (
      <div className={`zibby-video-container ${className}`}>
        <div className="zibby-video-placeholder zibby-video-placeholder--empty" role="status">
          <div className="zibby-video-placeholder__content">
            <p className="zibby-video-placeholder__title">No video available</p>
            <p className="zibby-video-placeholder__subtitle">
              A recording will appear here when this run produces one
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`zibby-video-container ${className}`}>
      <video
        ref={videoRef}
        src={videoSrc}
        onClick={togglePlayPause}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        className="zibby-video-player"
        style={{ cursor: 'pointer' }}
      />

      {showIcon && (
        <div 
          className={`zibby-play-pause-overlay ${showIcon === 'play' && iconFading ? 'zibby-fade-out' : ''}`}
          key={showIcon}
        >
          {showIcon === 'play' ? (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          ) : (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="white">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          )}
        </div>
      )}
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;

